import { useEffect, useState } from 'react';
import { getSmtpSettings, saveSmtpSettings, testSmtp } from '../../api/settings.api';

export default function SettingsEmail({ onFlash }) {
  const SMTP_PRESETS = [
    { key: 'gmail', label: 'Gmail / Google Workspace', host: 'smtp.gmail.com', port: 465, secure: true },
    { key: 'outlook', label: 'Outlook / Microsoft 365', host: 'smtp.office365.com', port: 587, secure: false },
    { key: 'ovh', label: 'OVH', host: 'ssl0.ovh.net', port: 465, secure: true },
    { key: 'zoho', label: 'Zoho', host: 'smtp.zoho.com', port: 465, secure: true },
    { key: 'ionos', label: 'IONOS', host: 'smtp.ionos.fr', port: 587, secure: false },
    { key: 'custom', label: 'Autre (personnalise)', host: '', port: 587, secure: false }
  ];

  const getDefaultPresetKey = () => 'gmail';
  const getPresetByKey = (key) => SMTP_PRESETS.find(p => p.key === key) || SMTP_PRESETS[0];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [stepSaved, setStepSaved] = useState(false);
  const [stepTested, setStepTested] = useState(false);
  const [presetKey, setPresetKey] = useState(getDefaultPresetKey());
  const [form, setForm] = useState({
    smtp_host: getPresetByKey(getDefaultPresetKey()).host,
    smtp_port: getPresetByKey(getDefaultPresetKey()).port,
    smtp_user: '',
    smtp_pass: '',
    smtp_secure: getPresetByKey(getDefaultPresetKey()).secure,
    smtp_from: '',
    smtp_enabled: true
  });
  const [hasPassword, setHasPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getSmtpSettings();
      let nextPresetKey = 'custom';
      if (data.smtp_host) {
        const matched = SMTP_PRESETS.find(p => p.host && p.host === data.smtp_host);
        nextPresetKey = matched ? matched.key : 'custom';
      }
      setForm(f => ({
        ...f,
        smtp_host: data.smtp_host || getPresetByKey(getDefaultPresetKey()).host,
        smtp_port: data.smtp_port || getPresetByKey(getDefaultPresetKey()).port,
        smtp_user: data.smtp_user || '',
        smtp_secure: data.smtp_secure != null ? !!data.smtp_secure : getPresetByKey(getDefaultPresetKey()).secure,
        smtp_from: data.smtp_from || '',
        smtp_enabled: data.smtp_enabled !== false,
        smtp_pass: ''
      }));
      setPresetKey(nextPresetKey);
      setHasPassword(!!data.hasPassword);
    } catch (e) {
      onFlash?.('❌ Erreur chargement SMTP');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.smtp_pass) delete payload.smtp_pass;
      const data = await saveSmtpSettings(payload);
      setHasPassword(!!data.hasPassword);
      setForm(f => ({ ...f, smtp_pass: '' }));
      setStepSaved(true);
      onFlash?.('✅ SMTP enregistré');
    } catch (e) {
      onFlash?.('❌ Erreur sauvegarde SMTP');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!testEmail) {
      onFlash?.('❌ Email de test requis');
      return;
    }
    setTesting(true);
    try {
      await testSmtp(testEmail);
      setStepTested(true);
      onFlash?.('✅ Test SMTP envoyé');
    } catch (e) {
      onFlash?.('❌ Test SMTP échoué');
    } finally {
      setTesting(false);
    }
  };

  const stepFilled = !!(form.smtp_host && form.smtp_user && (form.smtp_from || form.smtp_user));
  const progressCount = [stepFilled, stepSaved, stepTested].filter(Boolean).length;

  if (loading) return <div className="p-4">Chargement…</div>;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Paramètres</div>
            <div className="text-lg font-semibold text-slate-900">Email sortant (SMTP)</div>
            <div className="text-sm text-slate-600">Configuration par tenant, sans action serveur.</div>
          </div>
          <div className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
            Sécurisé et isolé
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Guide client interactif</div>
            <div className="text-xs text-slate-800">Suivez les 3 etapes pour activer l’envoi email.</div>
          </div>
          <button
            onClick={() => setShowGuide(v => !v)}
            className="text-xs px-3 py-1 rounded border border-slate-300 text-slate-900 bg-white"
          >
            {showGuide ? 'Masquer' : 'Afficher'}
          </button>
        </div>

        {showGuide && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className={`rounded-xl border p-3 ${stepFilled ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
              <div className="text-xs uppercase text-slate-500">Etape 1</div>
              <div className="text-sm font-semibold text-slate-900">Renseigner le SMTP</div>
              <div className="text-xs text-slate-600">Host, user, from, port.</div>
              <div className="mt-2 text-xs">
                Statut: {stepFilled ? 'OK' : 'A completer'}
              </div>
            </div>
            <div className={`rounded-xl border p-3 ${stepSaved ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
              <div className="text-xs uppercase text-slate-500">Etape 2</div>
              <div className="text-sm font-semibold text-slate-900">Enregistrer</div>
              <div className="text-xs text-slate-600">Sauvegarder la config.</div>
              <div className="mt-2 text-xs">
                Statut: {stepSaved ? 'OK' : 'En attente'}
              </div>
            </div>
            <div className={`rounded-xl border p-3 ${stepTested ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
              <div className="text-xs uppercase text-slate-500">Etape 3</div>
              <div className="text-sm font-semibold text-slate-900">Tester</div>
              <div className="text-xs text-slate-600">Envoyer un email de test.</div>
              <div className="mt-2 text-xs">
                Statut: {stepTested ? 'OK' : 'En attente'}
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 text-xs text-slate-900">
          Progression: {progressCount}/3
        </div>

        {showGuide && (
          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3">
            <div className="text-xs font-semibold text-slate-900">Mot de passe d’application</div>
            <div className="text-xs text-slate-800">
              Pour Gmail/Outlook, utilisez un mot de passe d’application (2FA requis).
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <a
                className="text-xs px-3 py-1 rounded border border-slate-300 text-slate-900 bg-slate-50"
                href="https://myaccount.google.com/apppasswords"
                target="_blank"
                rel="noreferrer"
              >
                Google App Passwords
              </a>
              <a
                className="text-xs px-3 py-1 rounded border border-slate-300 text-slate-900 bg-slate-50"
                href="https://support.microsoft.com/en-us/account-billing/how-to-get-and-use-app-passwords-5896ed9b-4263-e681-128a-a6f2979a7944"
                target="_blank"
                rel="noreferrer"
              >
                Microsoft App Passwords
              </a>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4">
            <div className="text-sm font-semibold text-slate-900">Connexion SMTP</div>
            <div className="text-xs text-slate-500">Les identifiants sont chiffrés côté serveur.</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs text-slate-600 mb-1">Fournisseur SMTP</label>
              <select
                className="px-3 py-2 border rounded pilot-input w-full"
                value={presetKey}
                onChange={e => {
                  const nextKey = e.target.value;
                  const preset = getPresetByKey(nextKey);
                  setPresetKey(nextKey);
                  if (preset.key !== 'custom') {
                    setForm({
                      ...form,
                      smtp_host: preset.host,
                      smtp_port: preset.port,
                      smtp_secure: preset.secure
                    });
                  }
                }}
              >
                {SMTP_PRESETS.map(p => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </select>
            </div>
            <input className="px-3 py-2 border rounded pilot-input" placeholder="smtp.gmail.com" value={form.smtp_host}
              onChange={e => setForm({ ...form, smtp_host: e.target.value })} />
            <input className="px-3 py-2 border rounded pilot-input" placeholder="Port" type="number" value={form.smtp_port}
              onChange={e => setForm({ ...form, smtp_port: parseInt(e.target.value || '0', 10) })} />
            <input className="px-3 py-2 border rounded pilot-input" placeholder="User" value={form.smtp_user}
              onChange={e => setForm({ ...form, smtp_user: e.target.value })} />
            <input className="px-3 py-2 border rounded pilot-input" placeholder={hasPassword ? 'Mot de passe (déjà défini)' : 'Mot de passe'} type="password" value={form.smtp_pass}
              onChange={e => setForm({ ...form, smtp_pass: e.target.value })} />
            <input className="px-3 py-2 border rounded pilot-input" placeholder="From (ex: support@domaine.tld)" value={form.smtp_from}
              onChange={e => setForm({ ...form, smtp_from: e.target.value })} />
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.smtp_secure} onChange={e => setForm({ ...form, smtp_secure: e.target.checked })} />
                SMTP Secure (465)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.smtp_enabled} onChange={e => setForm({ ...form, smtp_enabled: e.target.checked })} />
                Activer SMTP
              </label>
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <button onClick={save} disabled={saving} className="px-4 py-2 rounded bg-slate-900 text-white">
              {saving ? '...' : 'Enregistrer'}
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-900 p-5 text-white">
          <div className="text-sm font-semibold">Conseils rapides</div>
          <div className="mt-2 text-xs text-slate-200">
            Utilisez un compte dédié et vérifiez le “From” pour éviter les rejets.
          </div>
          <div className="mt-4 rounded-xl bg-white/10 p-3 text-xs">
            Aucun impact sur campagnes, queue ou statistiques.
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
        <div className="text-sm font-semibold text-emerald-900">Test SMTP</div>
        <div className="text-xs text-emerald-700">Envoi direct d’un email de test. Aucun envoi réel.</div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <input className="px-3 py-2 border rounded w-72 pilot-input" placeholder="Email de test" value={testEmail}
            onChange={e => setTestEmail(e.target.value)} />
          <button onClick={runTest} disabled={testing} className="px-3 py-2 rounded bg-emerald-600 text-white">
            {testing ? '...' : 'Tester'}
          </button>
        </div>
      </div>
    </div>
  );
}
